import { useState, useEffect, useCallback } from "react";
import { axiosInstance, API_ENDPOINTS, getAuthHeaders } from "config/api";

export const useAuditLogs = (filters = {}) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    from = "",
    to = "",
    entity_type = "",
    action = "",
    clinic_id = null,
    page = 1,
    limit = 20,
    enabled = true,
  } = filters;

  const fetchLogs = useCallback(
    async (signal) => {
      if (!enabled) {
        setLogs([]);
        setTotal(0);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const params = { page, limit };
        if (from) params.from = from;
        if (to) params.to = to;
        if (entity_type) params.entity_type = entity_type;
        if (action) params.action = action;
        if (clinic_id) params.clinic_id = clinic_id;

        const response = await axiosInstance.get(API_ENDPOINTS.AUDIT.LOGS, {
          headers: getAuthHeaders(),
          params,
          signal,
          validateStatus: (status) => status < 500,
        });

        if (response.status >= 200 && response.status < 300 && response.data?.success) {
          const rows = Array.isArray(response.data.data) ? response.data.data : [];
          setLogs(
            rows.map((row) => ({
              ...row,
              actor_label:
                row.actor?.email ||
                (row.actor?.id != null
                  ? `${row.actor.role || "user"} #${row.actor.id}`
                  : row.actor?.role || "anonymous"),
              entity_label: row.entity_id
                ? `${row.entity_type} #${row.entity_id}`
                : row.entity_type,
            })),
          );
          setTotal(Number(response.data.total || 0));
          setError(null);
        } else {
          setLogs([]);
          setTotal(0);
          setError(new Error(response.data?.error || "Failed to load audit logs"));
        }
      } catch (err) {
        if (err.name === "CanceledError" || err.message === "canceled") return;
        console.error("Error fetching audit logs:", err);
        setLogs([]);
        setTotal(0);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [from, to, entity_type, action, clinic_id, page, limit, enabled],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchLogs(controller.signal);
    return () => controller.abort();
  }, [fetchLogs]);

  return {
    logs,
    total,
    isLoading,
    error,
    refreshLogs: () => fetchLogs(),
  };
};
